import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Select, Table } from "antd";
import IntlMessages from "@util/IntlMessages";
import "./GroupsConfig.less";
import {
   getBoard,
   removeShareBoardWithGroup,
   shareBoardWithGroup,
   updateSharedBoardWithGroup,
   getB2ngGroups
} from "../../../../../../../services/restAPI/RestApi";
import { useIntl } from "react-intl";
import { Message, Button, Popover, Switch, Popconfirm } from "../../../../../../BP2Components";
import { Icon_Delete, Icon_Save } from "../../../../../../BP2Components/Icons/icons";

const { Option } = Select;

const filterUniqueGroups = (groups) => {
   const uniqueGroups = [];
   const groupSet = new Set();

   if(!groups) return uniqueGroups;
 
   groups.forEach(group => {
     const groupKey = `${group.name}-${group.oldId}-${group.companyOldId}`;
     if (!groupSet.has(groupKey)) {
       groupSet.add(groupKey);
       uniqueGroups.push(group);
     }
   });
 
   return uniqueGroups;
 }; 

const GroupsConfig = ({ boardInfo, setShareOption }) => {
   const intl = useIntl();
   const [groups, setGroups] = useState([]);
   const [dataSource, setDataSource] = useState();
   const [permissionSettings, setPermissionSettings] = useState({
      group: "",
      showInMainNav: false,
      role: "",
      // "isReferenceGroup" is parameter to define if we sharing board to all groups with the same name as the one we currently sharing
      isReferenceGroup: false
   });

   const handleDelete = (groupId, boardId, role) => () => {
      Message.loading(intl.formatMessage({ id: "global.loading" }), 0);
      removeShareBoardWithGroup(boardId, groupId, role)
         .then(() => {
            setDataSource((prevDataSource) =>
               prevDataSource.filter((singleData) => singleData?.id !== groupId)
            );
            Message.destroy();
            Message.success(intl.formatMessage({ id: "global.done" }));
         })
         .catch(() => {
            Message.destroy();
            Message.error(intl.formatMessage({ id: "global.error" }));
         });
   };

   const saveBoardGroupRole = (groupInfo, boardId) => () => {
      const { id: groupId, permission, showInMainNav, isReferenceGroup } = groupInfo;
      Message.loading(intl.formatMessage({ id: "global.loading" }), 0);
      updateSharedBoardWithGroup(
         boardId,
         groupId,
         permission,
         showInMainNav ?? false,
         isReferenceGroup
      )
         .then(() => {
            Message.destroy();
            Message.success(intl.formatMessage({ id: "global.done" }));
         })
         .catch(() => {
            Message.destroy();
            Message.error(intl.formatMessage({ id: "global.error" }));
         });
   };

   const handleChangeGroupShareSettings = (groupId, settingName) => (value) => {
      try {
         setDataSource((prevDataSource) => {
            const newDataSource = prevDataSource;
            const indexOfData = newDataSource.findIndex((singleData) => singleData.id === groupId);
            newDataSource[indexOfData][settingName] = value;
            return newDataSource;
         });
      } catch (err) {
         Message.error(intl.formatMessage({ id: "global.error" }));
      }
   };

   const handleSavePermissions = (settings, boardId) => () => {
      const { group: groupInfo, role, showInMainNav, isReferenceGroup } = settings;
      Message.loading(intl.formatMessage({ id: "global.loading" }), 0);
      shareBoardWithGroup(
         boardId,
         role,
         JSON.parse(groupInfo),
         showInMainNav ?? false,
         isReferenceGroup
      )
         .then((res) => {
            const groupsInfo = res.data.boardGroups?.map((group) => ({
               id: group?.id,
               name: group?.group?.name,
               showInMainNav: group?.showInMainNav,
               permission: group?.memberType,
               isReferenceGroup: group?.isReferenceGroup
            }));
            setDataSource(groupsInfo);
            Message.destroy();
            Message.success(intl.formatMessage({ id: "global.done" }));
         })
         .catch(() => {
            Message.destroy();
            Message.error(intl.formatMessage({ id: "global.error" }));
         });
   };

   const columns = [
      {
         title: <IntlMessages id="boardsManager.name" />,
         dataIndex: "name",
         key: "name"
      },
      {
         title: <IntlMessages id="boardsManager.permission" />,
         dataIndex: "permission",
         key: "permission",
         render: (_, record) => (
            <Select
               defaultValue={record.permission}
               style={{ width: 150 }}
               onSelect={handleChangeGroupShareSettings(record.id, "permission")}
            >
               <Option value="OWNER">
                  <IntlMessages id="boardsManager.owner" />
               </Option>
               <Option value="MODERATOR">
                  <IntlMessages id="boardsManager.moderator" />
               </Option>
               <Option value="COLLABOLATOR">
                  <IntlMessages id="boardsManager.collabolator" />
               </Option>
            </Select>
         )
      },
      {
         title: <IntlMessages id="boardsManager.showInMainNav" />,
         dataIndex: "showInMainNav",
         key: "showInMainNav",
         render: (_, record) => (
            <Switch
               defaultChecked={record.showInMainNav}
               onChange={handleChangeGroupShareSettings(record.id, "showInMainNav")}
            />
         )
      },
      {
         title: <IntlMessages id="boardsManager.isReferenceGroup" />,
         dataIndex: "isReferenceGroup",
         key: "isReferenceGroup",
         render: (_, record) => (
            <div style={{ textAlign: "center" }}>
               <Switch
                  defaultChecked={record.isReferenceGroup}
                  onChange={handleChangeGroupShareSettings(record.id, "isReferenceGroup")}
               />
            </div>
         )
      },
      {
         title: <IntlMessages id="global.actions" />,
         dataIndex: "actions",
         key: "actinos",
         render: (_, record) => (
            <div style={{ display: "grid", gap: "0.5rem" }}>
               <Popover
                  title={intl.formatMessage({ id: "boardsManager.savePermissions" })}
                  placement="left"
                  trigger="hover"
                  dark={true}
               >
                  <Button
                     buttonType="primary"
                     onClick={saveBoardGroupRole(record, boardInfo.id)}
                     className="button"
                  >
                     <Icon_Save />
                  </Button>
               </Popover>
               <Popover
                  title={intl.formatMessage({ id: "boardsManager.deletePermissions" })}
                  placement="left"
                  trigger="hover"
                  dark={true}
               >
                  <Popconfirm
                     title={<IntlMessages id="settings.sureToDelete" />}
                     onConfirm={handleDelete(record.id, boardInfo.id, record.permission)}
                  >
                     <Button buttonType="danger" ghost className="button">
                        <Icon_Delete />
                     </Button>
                  </Popconfirm>
               </Popover>
            </div>
         )
      }
   ];

   useEffect(() => {
      getB2ngGroups().then((res) => {
         const uniqueGroups = filterUniqueGroups(res.data);
         setGroups(uniqueGroups);
      });
      getBoard(boardInfo.id).then((res) => {
         const groupsInfo = res.data.boardGroups?.map((group) => ({
            id: group?.id,
            name: group?.group?.name,
            showInMainNav: group?.showInMainNav,
            permission: group?.memberType,
            isReferenceGroup: group?.isReferenceGroup
         }));
         setDataSource(groupsInfo);
      });
   }, []);

   return (
      <div className="groups-config">
         <div className="groups-config__selection">
            <IntlMessages id="boardsManager.selectGroup" />
            <Select
               loading={!groups?.length}
               onChange={(group) =>
                  setPermissionSettings((prevSettings) => ({ ...prevSettings, group }))
               }
               showSearch
               filterOption={(input, option) =>
                  option?.props?.children?.toLowerCase().includes(input.toLowerCase())
               }
            >
               {groups?.map((group) => (
                  <Option key={group.oldId} value={JSON.stringify(group)}>
                     {group.name}
                  </Option>
               ))}
            </Select>
         </div>
         <div className="groups-config__permission">
            <div className="groups-config__permission__select">
               <IntlMessages id="boardsManager.selectPermission" />
               <Select
                  onChange={(role) =>
                     setPermissionSettings((prevSettings) => ({ ...prevSettings, role }))
                  }
               >
                  <Option value="OWNER">
                     <IntlMessages id="boardsManager.owner" />
                  </Option>
                  <Option value="MODERATOR">
                     <IntlMessages id="boardsManager.moderator" />
                  </Option>
                  <Option value="COLLABOLATOR">
                     <IntlMessages id="boardsManager.collabolator" />
                  </Option>
               </Select>
            </div>
            <div className="groups-config__permission__switch">
               <IntlMessages id="boardsManager.showInMainNav" />
               <Switch
                  style={{ display: "block", margin: "auto" }}
                  onChange={(showInMainNav) =>
                     setPermissionSettings((prevSettings) => ({ ...prevSettings, showInMainNav }))
                  }
               />
            </div>
            <div className="groups-config__permission__switch">
               <IntlMessages id="boardsManager.isReferenceGroup" />
               <Switch
                  style={{ display: "block", margin: "auto" }}
                  onChange={(isReferenceGroup) =>
                     setPermissionSettings((prevSettings) => ({
                        ...prevSettings,
                        isReferenceGroup
                     }))
                  }
               />
            </div>
            <Button
               buttonType="primary"
               className="groups-config__permission__save"
               onClick={handleSavePermissions(permissionSettings, boardInfo.id)}
            >
               <IntlMessages id="boardsManager.savePermissions" />
            </Button>
            {(permissionSettings.role === "OWNER" || permissionSettings.role === "MODERATOR") && (
               <div className="users-config__permission__describe">
                  <IntlMessages id="boardsManager.ownerAndModeratorPermission" />
               </div>
            )}
            {permissionSettings.role === "COLLABOLATOR" && (
               <div className="users-config__permission__describe">
                  <IntlMessages id="boardsManager.collaboratorPermission" />
               </div>
            )}
         </div>
         <Table
            size="small"
            dataSource={dataSource}
            loading={!dataSource}
            columns={columns}
            pagination={false}
         />

         <Button
            htmlType="button"
            ghost
            buttonType="primary"
            onClick={() => setShareOption()}
            className="button"
         >
            <IntlMessages id="global.goBack" />
         </Button>
      </div>
   );
};

GroupsConfig.propTypes = {
   boardInfo: PropTypes.object.isRequired,
   setShareOption: PropTypes.func.isRequired
};

export default GroupsConfig;
